import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Button from '@/components/base/Button';
import Modal from '@/components/base/Modal';
import { useAuth } from '@/context/AuthContext';
import { getActiveTask, isActiveTaskStatus } from '@/services/enterpriseApi';
import type { ActiveTaskInfo } from '@/services/enterpriseApi';

/**
 * 登录后提示「有任务正在处理中，是否前往查看」。
 *
 * 为什么挂在全局：登录是全局弹窗（`LoginModal`），没有独立的登录成功页；
 * 由本组件监听 `user` 变化，在**每次登录后**查一次 `GET /tasks/active`。
 *
 * 异常处理：
 * - 网络失败 → `getActiveTask()` 内部吞掉并返回 null，绝不阻断登录；
 * - 同一**任务**在一个标签页会话内只提示一次（落 `sessionStorage`）。原先用
 *   `useRef` 去重，而组件随文档刷新重建、ref 归零，导致分析过程中每次 F5
 *   都重新弹一次——「反复刷新反复弹」即由此而来；
 * - **在 `/single-analysis` 分析页时不弹**：该页自己负责提交/续轮询/恢复快照，
 *   弹窗只会打断（刷新后「刷新保护」还会 replace 到 `?taskId=&resume=1`，
 *   按路径判断同时消除了弹窗与这次跳转的时序竞态）；
 * - 用户点「稍后」即关闭，不再重复打扰。
 */
const PROMPTED_KEY_PREFIX = 'tax-insight:active-task-prompted:';

function promptedStorageKey(userId: string): string {
  return `${PROMPTED_KEY_PREFIX}${userId}`;
}

/** 本会话已为该用户提示过的任务号；sessionStorage 不可用时返回 null（降级为仅本次挂载去重）。 */
function readPromptedTaskId(userId: string): string | null {
  try {
    return sessionStorage.getItem(promptedStorageKey(userId));
  } catch {
    return null;
  }
}

function markPrompted(userId: string, taskId: string): void {
  try {
    sessionStorage.setItem(promptedStorageKey(userId), taskId);
  } catch {
    /* 隐私模式 / 配额超限：忽略，退化为当前挂载内去重 */
  }
}

/**
 * 是否已停在分析页：该页自己负责提交 / 续轮询 / 恢复快照。
 *
 * 这里按**路径**而非 `taskId` 精确匹配，因为刷新后「刷新保护」会 replace 到
 * `?taskId=&resume=1`，弹窗请求与这次跳转存在竞态；按路径判断可彻底消除
 * 「刚跳到任务页又被弹窗覆盖」的时序问题。分析页上的任务无需二次提示。
 */
function isOnAnalysisPage(pathname: string): boolean {
  return pathname === '/single-analysis';
}

export function ActiveTaskPrompt() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [task, setTask] = useState<ActiveTaskInfo | null>(null);
  const [open, setOpen] = useState(false);
  // 只依赖 id 字符串：AuthProvider 的 fetchMe 会换掉 user 对象引用，依赖 user 会让
  // 刷新时对 /tasks/active 多发一次请求（同一个 id 语义上不该重查）
  const userId = user?.id ?? null;
  // 本次挂载内已查询过的 userId：防止 StrictMode 双执行 / 重复渲染导致重复请求
  const queriedForRef = useRef<string | null>(null);
  // effect 只依赖 userId，弹窗前需要读到**最新**路由，故用 ref 传递
  const locationRef = useRef(location);

  // 渲染期写 ref 在并发渲染下可能读到未提交的值，改为 effect 同步（须声明在查询 effect 之前）
  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  // 弹窗已打开时用户自行导航到分析页（浏览器前进/后退）：立即关闭，别盖住进度页
  useEffect(() => {
    if (isOnAnalysisPage(location.pathname)) setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!userId) {
      queriedForRef.current = null;
      setTask(null);
      setOpen(false);
      return;
    }
    if (queriedForRef.current === userId) return;

    let cancelled = false;
    void (async () => {
      const active = await getActiveTask();
      if (cancelled) return;
      // 查询完成才记账：请求失败时保持可重试，不因一次网络抖动永久静默
      queriedForRef.current = userId;
      if (!active) return;
      // 双保险：后端已只返回运行中任务，这里再判一次避免终态竞态
      if (!isActiveTaskStatus(active.status)) return;
      // 已在分析页：页面自身在提交/续轮询/恢复，不要用弹窗打断
      if (isOnAnalysisPage(locationRef.current.pathname)) return;
      // 同一任务在本标签页会话内只提示一次：刷新后 ref 归零，靠这里兜住
      if (readPromptedTaskId(userId) === active.id) return;
      markPrompted(userId, active.id);
      setTask(active);
      setOpen(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (!task) return null;

  const company = task.companyName || '当前分析';
  const statusLabel =
    task.status === 'pending' ? '排队中' : '分析中';

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      title="有任务正在处理中"
      width="max-w-md"
    >
      <div className="space-y-4 text-sm text-ink-secondary">
        <p>
          「<span className="font-medium text-ink-primary">{company}</span>」的
          {statusLabel}任务尚未完成（进度 {Math.max(0, Math.min(100, task.progress ?? 0))}%）。
        </p>
        <p>是否现在前往查看？直接关闭也不会丢失任务，稍后可在「我的分析」里继续查看。</p>
      </div>
      <div className="mt-6 flex justify-end gap-3">
        <Button variant="ghost" onClick={() => setOpen(false)}>
          稍后
        </Button>
        <Button
          onClick={() => {
            setOpen(false);
            navigate(`/single-analysis?taskId=${encodeURIComponent(task.id)}&resume=1`);
          }}
        >
          前往查看
        </Button>
      </div>
    </Modal>
  );
}

export default ActiveTaskPrompt;
